import { FilterSection } from "../../../../data-model/filterValues";
import { MedicalPaFilterKey } from "../../../../enums/medicalPAFilterKeys";
import { isExceptionFlowForUrologyOrg } from "../../../../enums/organizationTypes";

/** Section header shown for the regimen-name filter on Sunstate Urology worklists. */
const SUNSTATE_REGIMEN_NAME_LABEL = "Appointment Type";

/** Section header for BO status filter on Sunstate (matches Estimate table column). */
const SUNSTATE_ESTIMATE_FILTER_LABEL = "Estimate";

/**
 * Filter sections hidden from the Sunstate Urology worklist filter panel,
 * keyed by their `FilterSection.id` (a `MedicalPaFilterKey` value).
 */
const SUNSTATE_HIDDEN_FILTER_IDS: ReadonlySet<string> = new Set([
  MedicalPaFilterKey.RegimenType,
  MedicalPaFilterKey.OrderStateStatus,
  MedicalPaFilterKey.ReviewStatus,
]);

/**
 * Applies urology exception-flow overrides to the worklist filter panel.
 *
 * For orgs in `exceptionFlowForUrologyOrgs` the regimen-name section is
 * relabelled to "Appointment Type", the BO status section to "Estimate", and
 * the Regimen Type, Order Status, and Review Status sections are removed.
 * Every other org receives the sections unchanged.
 *
 * The shared filter create-functions (`filterOptionsGenrator.ts`) stay org
 * agnostic; this post-processor is the single place org-specific tweaks live,
 * so it can be applied uniformly across the medical PA orders and generic
 * auth-status worklists.
 *
 * @param sections - Filter sections built from the filter creation config
 * @param orgId - Organization/facility ID for the current session
 * @returns Filter sections with exception-flow overrides applied, or the
 *   input unchanged for every other org
 */
export const applySunstateUrologyFilterOverrides = (
  sections: FilterSection[],
  orgId: string,
): FilterSection[] => {
  if (!isExceptionFlowForUrologyOrg(orgId)) {
    return sections;
  }

  return sections
    .filter((section) => !SUNSTATE_HIDDEN_FILTER_IDS.has(section.id))
    .map((section) => {
      if (section.id === MedicalPaFilterKey.RegimenNames) {
        return { ...section, label: SUNSTATE_REGIMEN_NAME_LABEL };
      }
      if (section.id === MedicalPaFilterKey.BoStatus) {
        return { ...section, label: SUNSTATE_ESTIMATE_FILTER_LABEL };
      }
      return section;
    });
};
